import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Clock, HardDrive, Calendar, User, Tag,
  Smartphone, BookOpen, CheckCircle2, Download, Edit3, UserPlus, Trash2, ExternalLink,
  Video, PlayCircle, AlertCircle,
} from 'lucide-react';
import Modal from '../../components/common/Modal';
import Loading from '../../components/common/Loading';
import { useAppContext } from '../../context/AppContext';
import { useRoleData } from '../../hooks/useRoleData';
import { hasPermission, PERMISSIONS } from '../../utils/rbac';
import { contentAPI, modulesAPI, explainerVideosAPI } from '../../services/api';
import VideoPlayerModal from '../../components/common/VideoPlayerModal';
import './ContentDetails.css';
import { formatDate } from '../../utils/dateFormatter';
import { CONTENT_CATEGORIES as defaultCategories, VR_PLATFORMS as defaultPlatforms } from '../../constants/enums';
import { BASE_URL } from '../../constants/config';

// Placeholder image URL
const PLACEHOLDER_IMAGE = `${BASE_URL}/uploads/assets/placehold_content.svg`;

const ContentDetails = () => {
  const { id } = useParams();
  const contentId = id;
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user navigated from within the app (has location.state)
  const hasNavigation = !!location.state;
  
  // Smart back navigation: use browser history only if navigated from within app
  const handleBackNavigation = () => {
    navigate(-1);
  };
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [moduleAssignmentModalOpen, setModuleAssignmentModalOpen] = useState(false);
  const [selectedModuleId, setSelectedModuleId] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailInputRef = useRef(null);
  const [categories, setCategories] = useState(defaultCategories);
  const [platforms, setPlatforms] = useState(defaultPlatforms);
  const [savingEdit, setSavingEdit] = useState(false);
  const [addingToModule, setAddingToModule] = useState(false);
  const [removingFromModule, setRemovingFromModule] = useState(false);
  const [attachedVideos, setAttachedVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [removingVideo, setRemovingVideo] = useState(false);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    category: '',
    platform: '',
    duration: '',
    tags: '',
    apkVersion: '',
    contentType: 'vr_app',
    url: '',
    status: '',
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        console.log('Fetching categories and platforms...');
        const [categoriesRes, platformsRes] = await Promise.all([
          contentAPI.getCategories(),
          contentAPI.getPlatforms()
        ]);
        const fetchedCategories = categoriesRes.data.data;
        const fetchedPlatforms = platformsRes.data.data;
        console.log('Fetched categories:', fetchedCategories);
        console.log('Fetched platforms:', fetchedPlatforms);

        // Use fetched data if non-empty, otherwise fallback to defaults
        setCategories(fetchedCategories && fetchedCategories.length > 0 ? fetchedCategories : defaultCategories);
        setPlatforms(fetchedPlatforms && fetchedPlatforms.length > 0 ? fetchedPlatforms : defaultPlatforms);
      } catch (error) {
        console.error('Failed to fetch options:', error);
        console.log('Using fallback categories and platforms');
        setCategories(defaultCategories);
        setPlatforms(defaultPlatforms);
      }
    };
    fetchOptions();
  }, []);

  // Fetch attached explainer videos
  useEffect(() => {
    const fetchAttachedVideos = async () => {
      setLoadingVideos(true);
      try {
        const response = await explainerVideosAPI.getContentExplainerVideos(contentId);
        setAttachedVideos(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch attached videos:', error);
      } finally {
        setLoadingVideos(false);
      }
    };
    
    if (contentId) {
      fetchAttachedVideos();
    }
  }, [contentId]);

  useEffect(() => {
    console.log('Categories updated:', categories);
    console.log('Platforms updated:', platforms);
  }, [categories, platforms]);

  const { showNotification, currentUser } = useAppContext();
  const { filteredContent, filteredModules, filteredUsers, refreshData, loading, error } = useRoleData();

  if (loading) {
    return (
      <div className="empty-state">
        <Loading fullScreen={false} message="Loading content..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="empty-state">
        <h3>Error loading content</h3>
        <p>{error}</p>
        <button className="btn btn-outline" onClick={() => refreshData()}>
          Retry
        </button>
      </div>
    );
  }

  const content = filteredContent.find((c) => c.contentId && c.contentId.replace(/-/g, '') === id);

  if (!content) {
    return (
      <div className="empty-state">
        <h3>Content not found or you don't have access</h3>
        <button className="btn btn-outline" onClick={handleBackNavigation}>
          Back to Library
        </button>
      </div>
    );
  }

  const hasThumbnail = content.thumbnail && typeof content.thumbnail === 'string' && content.thumbnail.trim();

  const formattedUploadDate = formatDate(content.uploadDate);
  const uploader = filteredUsers.find(u => u.id === content.uploadedBy || u.name === content.uploadedBy);
  const uploadedByName = content.uploadedByName || (uploader ? (uploader.firstName && uploader.lastName ? `${uploader.firstName} ${uploader.lastName}` : uploader.name) : (content.uploadedBy || 'Unknown'));

  const associatedModules = filteredModules.filter((m) => content.moduleIds.includes(m.id));
  const availableModules = filteredModules.filter((m) => !content.moduleIds.includes(m.id));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddToModule = async () => {
    if (!selectedModuleId) {
      showNotification('Please select a module to add content to', 'error');
      return;
    }

    setAddingToModule(true);
    try {
      await modulesAPI.addContentToModule(selectedModuleId, contentId);
      showNotification(`Content added to module successfully!`, 'success');
      setSelectedModuleId('');
      // Refresh data
      refreshData();
    } catch (error) {
      console.error('Failed to add content to module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to add content to module. Please try again.',
        'error'
      );
    } finally {
      setAddingToModule(false);
    }
  };

  const handleRemoveFromModule = async (moduleId) => {
    setRemovingFromModule(true);
    try {
      await modulesAPI.removeContentFromModule(moduleId, contentId);
      showNotification(`Content removed from module successfully!`, 'success');
      // Refresh data
      refreshData();
    } catch (error) {
      console.error('Failed to remove content from module:', error);
      showNotification(
        error.response?.data?.error || 'Failed to remove content from module. Please try again.',
        'error'
      );
    } finally {
      setRemovingFromModule(false);
    }
  };

  const openVideoPlayerModal = (video) => {
    setSelectedVideo(video);
    setIsVideoModalOpen(true);
  };

  const closeVideoPlayerModal = () => {
    setIsVideoModalOpen(false);
    setSelectedVideo(null);
  };

  const handleRemoveVideo = async (videoId) => {
    if (!window.confirm('Remove this explainer video from the content?')) {
      return;
    }

    setRemovingVideo(true);
    try {
      await explainerVideosAPI.removeVideoFromContent(contentId, videoId);
      showNotification('Explainer video removed successfully!', 'success');
      setAttachedVideos((prev) => prev.filter((v) => v.id !== videoId));
    } catch (error) {
      console.error('Failed to remove video:', error);
      showNotification(
        error.response?.data?.error || 'Failed to remove video. Please try again.',
        'error'
      );
    } finally {
      setRemovingVideo(false);
    }
  };

  const handleEditOpen = () => {
    if (!hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT)) {
      showNotification('You do not have permission to edit content', 'error');
      return;
    }
    setForm({
      title: content.title,
      subtitle: content.subtitle,
      description: content.description,
      category: content.category,
      platform: content.platform,
      duration: content.duration.toString(),
      tags: content.tags.join(', '),
      apkVersion: content.apkVersion,
      contentType: content.contentType || 'vr_app',
      url: content.url || '',
      status: content.status,
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT)) {
      showNotification('You do not have permission to edit content', 'error');
      return;
    }
    // Basic validation
    if (!form.title.trim() || !form.description.trim() || !form.category.trim() || !form.platform.trim()) {
      showNotification('Title, description, category, and platform are required', 'error');
      return;
    }

    setSavingEdit(true);
    try {
      await contentAPI.updateContent(contentId, {
        title: form.title,
        subtitle: form.subtitle || '',
        description: form.description,
        category: form.category,
        platform: form.platform,
        duration: parseInt(form.duration) || 0,
        tags: form.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        apkVersion: form.apkVersion || '',
        contentType: form.contentType,
        url: form.url || '',
        status: form.status,
      });
      showNotification(`Content "${form.title}" updated successfully!`, 'success');
      setEditModalOpen(false);
      // Refresh content data
      refreshData();
    } catch (error) {
      console.error('Failed to update content:', error);
      showNotification(
        error.response?.data?.error || 'Failed to update content. Please try again.',
        'error'
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = () => {
    if (!hasPermission(currentUser, PERMISSIONS.DELETE_MODULES)) {
      showNotification('You do not have permission to delete content', 'error');
      return;
    }
    setDeleteConfirmationText('');
    setDeleteConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    // Validate confirmation text
    if (deleteConfirmationText.trim() !== 'delete content') {
      showNotification('Please type "delete content" exactly to confirm deletion', 'error');
      return;
    }

    setIsDeleting(true);
    try {
      await contentAPI.deleteContent(contentId);
      showNotification(`Content "${content.title}" deleted successfully`, 'success');
      setDeleteConfirmModalOpen(false);
      navigate('/content-library');
    } catch (error) {
      console.error('Delete content error:', error);
      showNotification(error.response?.data?.error || 'Failed to delete content', 'error');
    } finally {
      setIsDeleting(false);
      setDeleteConfirmationText('');
    }
  };

  const handleThumbnailChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showNotification('Please select a valid image file (JPEG, PNG, GIF, WEBP)', 'error');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('Image size should be less than 10MB', 'error');
      return;
    }

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      await contentAPI.uploadThumbnail(contentId, formData);
      showNotification('Thumbnail updated successfully', 'success');
      // Refresh data to get new thumbnail URL
      refreshData();
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      showNotification(
        error.response?.data?.error || 'Failed to upload thumbnail. Please try again.',
        'error'
      );
    } finally {
      setUploadingThumbnail(false);
      // Reset file input
      if (thumbnailInputRef.current) {
        thumbnailInputRef.current.value = '';
      }
    }
  };

  const handleDeleteThumbnail = async () => {
    if (!hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT)) {
      showNotification('You do not have permission to delete thumbnails', 'error');
      return;
    }
    if (!window.confirm('Are you sure you want to delete the thumbnail? This will remove the current thumbnail image.')) {
      return;
    }
    try {
      await contentAPI.deleteThumbnail(contentId);
      showNotification('Thumbnail deleted successfully', 'success');
      // Refresh data to reflect removed thumbnail
      refreshData();
    } catch (error) {
      console.error('Failed to delete thumbnail:', error);
      showNotification(
        error.response?.data?.error || 'Failed to delete thumbnail. Please try again.',
        'error'
      );
    }
  };

  return (
    <div className="content-details">
      {/* Back Navigation */}
      <div className="content-details__back-nav">
        {hasNavigation && (
          <>
            <button className="btn btn-ghost content-details__back" onClick={handleBackNavigation} title="Go back to previous page">
              <ArrowLeft size={16} />
              Back
            </button>
            <span className="content-details__back-separator">|</span>
          </>
        )}
        <button className="btn btn-link content-details__back-parent" onClick={() => navigate('/content-library')} title="Back to VR Library">
          VR Library
        </button>
      </div>

      {/* Hero */}
      <div className="content-details__hero card">
        <div className="content-details__hero-thumb">
          <img
            src={hasThumbnail ? content.thumbnail : PLACEHOLDER_IMAGE}
            alt={content.title}
          />
          <div className="content-details__hero-overlay" />
          {hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT) && (
            <>
              <input
                type="file"
                ref={thumbnailInputRef}
                onChange={handleThumbnailChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <div className="content-details__thumb-actions">
                <button
                  className="btn btn-outline btn-sm content-details__thumb-edit"
                  onClick={() => thumbnailInputRef.current?.click()}
                  disabled={uploadingThumbnail}
                >
                  {uploadingThumbnail ? 'Uploading...' : (hasThumbnail ? 'Change thumbnail' : 'Upload thumbnail')}
                </button>
                {hasThumbnail && (
                  <button
                    className="btn btn-danger btn-sm content-details__thumb-delete"
                    onClick={handleDeleteThumbnail}
                    disabled={uploadingThumbnail}
                  >
                    Delete thumbnail
                  </button>
                )}
              </div>
            </>
          )}
        </div>
        <div className="content-details__hero-info">
          <div className="content-details__hero-badges">
            <span className="badge badge-green">{content.category}</span>
            <span className={`badge ${content.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
              {content.status}
            </span>
            {content.contentType === 'web_virtual_tour' ? (
              <span className="badge badge-dark">🌐 Web Tour</span>
            ) : (
              <span className="badge badge-dark">v{content.apkVersion}</span>
            )}
          </div>
          <h1 className="content-details__hero-title">{content.title}</h1>
          {content.subtitle && <p className="content-details__hero-subtitle">{content.subtitle}</p>}
          <p className="content-details__hero-desc">{content.description}</p>
          <div className="content-details__hero-meta">
            <span><Calendar size={14} /> Uploaded {formattedUploadDate}</span>
            <span><User size={14} /> By {uploadedByName}</span>
            <span><Smartphone size={14} /> {content.platform}</span>
          </div>
          <div className="content-details__hero-actions">
            {content.contentType === 'web_virtual_tour' ? (
              <button
                className="btn btn-primary"
                onClick={() => window.open(content.url, '_blank', 'noopener,noreferrer')}
              >
                <ExternalLink size={15} />
                Open Virtual Tour
              </button>
            ) : (
              currentUser?.role === 'Administrator' && (
                <button className="btn btn-primary">
                  <Download size={15} />
                  Download APK
                </button>
              )
            )}
            {(currentUser?.role === 'Administrator' || currentUser?.role === 'Instructor') && (
              <button className="btn btn-primary" onClick={() => setModuleAssignmentModalOpen(true)}>
                <UserPlus size={15} />
                Add to Module
              </button>
            )}
            {hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT) && (
              <button className="btn btn-outline" onClick={handleEditOpen}>
                <Edit3 size={15} />
                Edit Content
              </button>
            )}
            {hasPermission(currentUser, PERMISSIONS.DELETE_MODULES) && (
              <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                <Trash2 size={15} />
                {isDeleting ? 'Deleting...' : 'Delete Content'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="content-details__grid">
        {/* Metadata */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Content Details
            </h3>
          </div>
          <div className="card-body">
            <div className="content-details__meta-list">
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon">
                  <Clock size={16} />
                </div>
                <div>
                  <span className="content-details__meta-label">Duration</span>
                  <span className="content-details__meta-value">{content.duration} minutes</span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon">
                  <HardDrive size={16} />
                </div>
                <div>
                  <span className="content-details__meta-label">File Size</span>
                  <span className="content-details__meta-value">{content.fileSize}</span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon">
                  <Smartphone size={16} />
                </div>
                <div>
                  <span className="content-details__meta-label">VR Platform</span>
                  <span className="content-details__meta-value">{content.platform}</span>
                </div>
              </div>
              <div className="content-details__meta-item">
                <div className="content-details__meta-icon">
                  <CheckCircle2 size={16} />
                </div>
                <div>
                  <span className="content-details__meta-label">Total Completions</span>
                  <span className="content-details__meta-value">{content.completionCount} trainees</span>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="content-details__tags-section">
              <div className="content-details__tags-header">
                <Tag size={14} />
                <span>Tags</span>
              </div>
              <div className="content-details__tags">
                {content.tags.map((t) => (
                  <span key={t} className="content-lib__tag">#{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Associated Modules */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              Used in Modules
            </h3>
            <span className="badge badge-dark">{associatedModules.length}</span>
          </div>
          <div className="card-body">
            {associatedModules.length === 0 ? (
              <div className="empty-state">
                <p>Not assigned to any module yet</p>
              </div>
            ) : (
              <div className="content-details__modules">
                {associatedModules.map((m) => (
                  <div
                    key={m.id}
                    className="content-details__module-item"
                    onClick={() => navigate(`/modules/${m.moduleId ? m.moduleId.replace(/-/g, '') : m.id}`, { state: { fromContent: true } })}
                  >
                    <div className="content-details__module-icon">
                      <BookOpen size={18} />
                    </div>
                    <div className="content-details__module-info">
                      <span className="content-details__module-title">{m.title}</span>
                      <div className="content-details__module-meta">
                        <span>{m.traineeIds.length} trainees</span>
                        <span>{m.completionRate}% complete</span>
                        <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {m.status}
                        </span>
                      </div>
                      <div className="progress-bar" style={{ marginTop: 6 }}>
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${m.completionRate}%` }}
                        />
                      </div>
                    </div>
                    <ArrowLeft size={16} style={{ transform: 'rotate(180deg)', color: 'var(--text-muted)' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Attached Explainer Videos */}
        <div className="card">
          <div className="card-header">
            <h3 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
              <Video size={16} style={{ marginRight: '4px', verticalAlign: 'text-bottom' }} />
              Attached Explainer Videos
            </h3>
            <span className="badge badge-dark">{attachedVideos.length}</span>
          </div>
          <div className="card-body">
            {loadingVideos ? (
              <div className="empty-state">
                <p>Loading videos...</p>
              </div>
            ) : attachedVideos.length === 0 ? (
              <div className="empty-state">
                <AlertCircle size={32} style={{ color: 'var(--text-muted)', marginBottom: '8px' }} />
                <p>No explainer videos attached yet</p>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Attach videos from the Explainer Videos page
                </p>
              </div>
            ) : (
              <div className="content-details__videos-list">
                {attachedVideos.map((video) => (
                  <div key={video.id} className="content-details__video-item">
                    <div className="content-details__video-thumb">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt={video.title} />
                      ) : (
                        <div className="content-details__video-placeholder">
                          <Video size={24} />
                        </div>
                      )}
                      <div className="content-details__video-overlay">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => openVideoPlayerModal(video)}
                          title="Play video"
                        >
                          <PlayCircle size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="content-details__video-info">
                      <h4 className="content-details__video-title">{video.title}</h4>
                      <p className="content-details__video-desc">{video.description}</p>
                      <div className="content-details__video-meta">
                        <span className="badge badge-gray">{video.category}</span>
                        {video.duration && (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                            {Math.floor(video.duration / 60)}:{String(video.duration % 60).padStart(2, '0')}
                          </span>
                        )}
                      </div>
                    </div>
                    {hasPermission(currentUser, PERMISSIONS.UPLOAD_CONTENT) && (
                      <button
                        className="btn btn-ghost btn-sm content-details__video-remove"
                        onClick={() => handleRemoveVideo(video.id)}
                        disabled={removingVideo}
                        title="Remove video from content"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Content Modal */}
      <Modal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Edit Content"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setEditModalOpen(false)}>Cancel</button>

            <button className="btn btn-primary" onClick={handleSaveEdit} disabled={savingEdit}>
              <Edit3 size={14} /> {savingEdit ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div className="form-group">
            <label className="form-label">Content Title *</label>
            <input
              className="form-input"
              type="text"
              name="title"
              value={form.title}
              onChange={handleChange}
              placeholder="e.g., Bonny Site Walkthrough"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Subtitle</label>
            <input
              className="form-input"
              type="text"
              name="subtitle"
              value={form.subtitle}
              onChange={handleChange}
              placeholder="e.g., Complete safety induction for new employees"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea
              className="form-textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Describe what trainees will learn in this VR experience..."
              rows={4}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className="form-select"
                name="category"
                value={form.category}
                onChange={handleChange}
                required
              >
                <option value="">Select category</option>
                {(categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">VR Platform *</label>
              <select
                className="form-select"
                name="platform"
                value={form.platform}
                onChange={handleChange}
                required
              >
                <option value="">Select platform</option>
                {(platforms || []).map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Content Type</label>
              <select
                className="form-select"
                name="contentType"
                value={form.contentType}
                onChange={handleChange}
              >
                <option value="vr_app">VR Application (APK)</option>
                <option value="web_virtual_tour">Web Virtual Tour (URL)</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Duration (minutes)</label>
              <input
                className="form-input"
                type="number"
                name="duration"
                value={form.duration}
                onChange={handleChange}
                placeholder="e.g., 45"
                min="1"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              {form.contentType === 'web_virtual_tour' ? (
                <>
                  <label className="form-label">Virtual Tour URL</label>
                  <input
                    className="form-input"
                    type="url"
                    name="url"
                    value={form.url}
                    onChange={handleChange}
                    placeholder="https://example.com/virtual-tour"
                  />
                </>
              ) : (
                <>
                  <label className="form-label">APK Version</label>
                  <input
                    className="form-input"
                    type="text"
                    name="apkVersion"
                    value={form.apkVersion}
                    onChange={handleChange}
                    placeholder="e.g., 2.1.0"
                  />
                </>
              )}
            </div>
            <div className="form-group">
              {/* Empty spacer to maintain grid layout */}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input
                className="form-input"
                type="text"
                name="tags"
                value={form.tags}
                onChange={handleChange}
                placeholder="e.g., endoscopy, procedure, simulation"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                name="status"
                value={form.status}
                onChange={handleChange}
              >
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>
          </div>

        </div>
      </Modal>

      {/* Manage Module Assignments */}
      <Modal
        isOpen={moduleAssignmentModalOpen}
        onClose={() => setModuleAssignmentModalOpen(false)}
        title="Manage Module Assignments"
        size="lg"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setModuleAssignmentModalOpen(false)}>Cancel</button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          {/* Currently Associated Modules */}
          <div>
            <h4 className="section-title">Currently Associated Modules</h4>
            {associatedModules.length === 0 ? (
              <div className="empty-state">
                <p>This content is not assigned to any modules yet.</p>
              </div>
            ) : (
              <div className="content-details__modules">
                {associatedModules.map((m) => (
                  <div
                    key={m.id}
                    className="content-details__module-item"
                  >
                    <div className="content-details__module-icon">
                      <BookOpen size={18} />
                    </div>
                    <div className="content-details__module-info">
                      <span className="content-details__module-title">{m.title}</span>
                      <div className="content-details__module-meta">
                        <span>{m.traineeIds.length} trainees</span>
                        <span>{m.completionRate}% complete</span>
                        <span className={`badge ${m.status === 'Active' ? 'badge-green' : 'badge-gray'}`}>
                          {m.status}
                        </span>
                      </div>
                    </div>
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={() => handleRemoveFromModule(m.id)}
                      disabled={removingFromModule}
                      style={{ marginLeft: 'auto' }}
                    >
                      <Trash2 size={14} /> {removingFromModule ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add to Another Module */}
          <div>
            <h4 className="section-title">Add to Another Module</h4>
            {availableModules.length === 0 ? (
              <div className="empty-state">
                <p>No other modules available to add this content.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Select Module</label>
                  <select
                    className="form-select"
                    value={selectedModuleId}
                    onChange={(e) => setSelectedModuleId(e.target.value)}
                  >
                    <option value="">Choose a module...</option>
                    {availableModules.map((m) => (
                      <option key={m.id} value={m.id}>{m.title} ({m.category})</option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleAddToModule}
                  disabled={!selectedModuleId || addingToModule}
                >
                  <UserPlus size={14} /> {addingToModule ? 'Adding...' : 'Add to Module'}
                </button>
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Delete Content Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModalOpen}
        onClose={() => {
          setDeleteConfirmModalOpen(false);
          setDeleteConfirmationText('');
        }}
        title="Delete Content"
        size="md"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => {
              setDeleteConfirmModalOpen(false);
              setDeleteConfirmationText('');
            }}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete Content'}
            </button>
          </div>
        }
      >
        {content && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <p>
              You are about to delete <strong>{content.title}</strong>. This action cannot be undone.
            </p>
            <p>
              This will permanently remove the content and all associated data including thumbnail and module associations.
            </p>
            <div className="form-group">
              <label className="form-label">
                Type <strong>"delete content"</strong> to confirm:
              </label>
              <input
                className="form-input"
                type="text"
                placeholder="Type 'delete content' here"
                value={deleteConfirmationText}
                onChange={(e) => setDeleteConfirmationText(e.target.value)}
                autoFocus
              />
              <small className="text-muted">
                This extra step prevents accidental deletion.
              </small>
            </div>
          </div>
        )}
      </Modal>

      {/* Video Player Modal */}
      {selectedVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={closeVideoPlayerModal}
          video={selectedVideo}
        />
      )}
    </div>
  );
};

export default ContentDetails;